import { useCallback, useEffect, useRef } from 'react'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { Patient } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { DOSESPOT_PATIENT_SYSTEM_PREFIX } from '@/model/middleware-types'
import { logError } from '@/utils/errorUtils'
import { doseSpotPrescriptionsQueryKey } from './doseSpotPrescriptionsQueryKey'

/**
 * Has this patient ever been launched into DoseSpot? The identifier's system is
 * clinic-scoped and the browser does not know the clinic id, so the prefix is the
 * whole test — which is exactly enough to keep the chart-view sync off the
 * overwhelming majority of charts that have never seen a prescription.
 */
export function hasDoseSpotPatientIdentifier(patient: Patient | undefined): boolean {
  return (
    patient?.identifier?.some((id) => id.system?.startsWith(DOSESPOT_PATIENT_SYSTEM_PREFIX)) ===
    true
  )
}

/**
 * Fires the DoseSpot write-back sync and returns immediately. **It never blocks and never
 * throws**: the chart renders stored data straight away, and the medications query
 * invalidates once the sync lands.
 *
 * Success is silent by design — no toast. A prescriber who just wrote a prescription does
 * not need to be told the chart caught up.
 */
export function useDoseSpotPrescriptionSync() {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const client = useClient()

  return useCallback(
    (patientId: string, options: { debounce?: boolean; notifyOnFailure?: boolean } = {}) => {
      void client
        .doseSpotSyncPrescriptions({
          patientId,
          ...(options.debounce && { debounce: true }),
        })
        .then(({ synced }) => {
          // Only when something actually changed: the chart-view trigger runs on every
          // qualifying chart open, and invalidating unconditionally would re-fetch the
          // medications list each time for nothing.
          if (synced === 0) return
          medplum.invalidateSearches('MedicationRequest')
          // The chart's DoseSpot rows are read through react-query, not Medplum's
          // search cache, so this is the invalidation that actually repaints them.
          void queryClient.invalidateQueries({
            queryKey: doseSpotPrescriptionsQueryKey(patientId),
          })
        })
        .catch((error: unknown) => {
          logError('dosespot', 'prescription write-back sync failed', error)
          // §11.9 — the modal-close path warns, the chart-view path logs only. The copy
          // stays non-blocking on purpose: the next chart view syncs, so there is genuinely
          // nothing for the provider to do.
          if (options.notifyOnFailure) {
            showNotification({
              color: 'orange',
              message:
                "Couldn't refresh prescriptions from DoseSpot — the chart will catch up automatically. No action needed.",
            })
          }
        })
    },
    [client, medplum, queryClient],
  )
}

/**
 * The chart-view half of §11.1's guarantee — *the chart is correct whenever anyone looks at
 * it*. A prescription written after the provider abandoned the modal lands on the next chart
 * open by anyone, including a viewer who cannot prescribe at all.
 *
 * Debounced server-side to ≥5 minutes, so re-opening a chart costs nothing. The extra guard
 * here is for React, not the server: a re-render must not re-fire, so this runs once per
 * mounted patient.
 *
 * > The honest caveat, stated rather than engineered around: a **never-viewed** chart stays
 * > un-synced, and non-chart readers (BeAssistant, CCDA export, `usePatientSummary`) can see
 * > a stale medication list until a view or a modal close triggers a sync. A cron sweep is
 * > the named future hardening step, deliberately not built in v1.
 */
export function useDoseSpotChartViewSync(patient: Patient | undefined): void {
  const sync = useDoseSpotPrescriptionSync()
  const syncedFor = useRef<string | undefined>(undefined)

  const patientId = patient?.id
  const mapped = hasDoseSpotPatientIdentifier(patient)

  useEffect(() => {
    if (!patientId || !mapped || syncedFor.current === patientId) return
    syncedFor.current = patientId
    sync(patientId, { debounce: true })
  }, [patientId, mapped, sync])
}
